import { type ReactNode, useCallback, useEffect, useState } from "react";

import { reportUnexpectedError } from "~/api/client";
import {
  type DramaDiscoveryResponse,
  type DramaSeries,
  type DramaWatchHistoryItem,
  fetchDramaDiscovery,
  fetchDramaSeriesList,
  fetchDramaWatchHistory,
} from "~/api/drama";
import { CinemaHero } from "~/components/common/cinema/CinemaHero";
import { ContinueCard } from "~/components/common/cinema/ContinueCard";
import { DramaCardDark } from "~/components/common/cinema/DramaCardDark";
import { TrendingCard } from "~/components/common/cinema/TrendingCard";
import { UnderlineTab } from "~/components/common/cinema/UnderlineTab";
import Footer from "~/components/common/Footer";
import PageContainer from "~/components/common/PageContainer";
import { CinemaHeroSkeleton } from "~/components/skeleton/CinemaHeroSkeleton";
import { DramaCardDarkSkeleton } from "~/components/skeleton/DramaCardDarkSkeleton";
import { genreTabs, primaryGenre, seriesGenres } from "~/lib/drama-genres";
import { useUserStore } from "~/lib/stores/user";
import { trackCinemaHomeView } from "~/lib/track-events";

const PLACEHOLDER_COUNT = 12;
const TRENDING_COUNT = 5;

/**
 * The catalogue: banners, Continue watching, Trending now, and the series grid under genre tabs.
 *
 * This route is prerendered with no query string, no session and no data, so the built HTML is
 * whatever renders before the fetch resolves — the skeletons below, never a default series.
 */
export default function CinemaCataloguePage() {
  const readToken = useCallback(() => useUserStore.getState().token, []);
  const [series, setSeries] = useState<DramaSeries[]>([]);
  const [discovery, setDiscovery] = useState<DramaDiscoveryResponse | null>(null);
  const [history, setHistory] = useState<DramaWatchHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  /** A category name; null is "For you", the whole list. */
  const [genre, setGenre] = useState<string | null>(null);

  useEffect(() => {
    trackCinemaHomeView();
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Settled separately: each call feeds its own rows, and one failing costs only those.
    Promise.allSettled([
      fetchDramaSeriesList(readToken),
      fetchDramaDiscovery(readToken),
      fetchDramaWatchHistory(readToken),
    ]).then(([list, disc, watched]) => {
      for (const result of [list, disc, watched]) {
        if (result.status === "rejected") reportUnexpectedError(result.reason);
      }
      if (cancelled) return;
      if (list.status === "fulfilled") setSeries(list.value.series ?? []);
      if (disc.status === "fulfilled") setDiscovery(disc.value);
      if (watched.status === "fulfilled") setHistory(watched.value.items ?? []);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [readToken]);

  const heroSlides = discovery?.banners?.length ? discovery.banners : series.slice(0, 1);
  const categories = discovery?.sections?.find(section => section.key === "categories")?.categories ?? [];
  const trending =
    discovery?.sections?.find(section => section.key === "trending_now")?.items?.slice(0, TRENDING_COUNT) ?? [];
  const tabs = genreTabs(series, categories);
  const shown = genre ? series.filter(item => seriesGenres(item, categories).includes(genre)) : series;

  return (
    <PageContainer
      headerProps={{ showBlidzAppMenu: true, showModeSwitch: true, showSearchBar: false, dark: true }}
      className="bg-black"
    >
      <h1 className="sr-only">Cinema</h1>
      {isLoading ? (
        <CinemaHeroSkeleton />
      ) : (
        heroSlides.length > 0 && <CinemaHero slides={heroSlides} categories={categories} />
      )}
      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-[28px] pt-[24px] pb-[32px] md:gap-[44px] md:px-[24px] md:pt-[44px] md:pb-[64px]">
        {history.length > 0 && (
          <section className="flex flex-col gap-[12px] md:gap-[18px]">
            <SectionHead title="Continue watching" />
            <div className="scrollbar-hide flex gap-[12px] overflow-x-auto px-[16px] md:gap-[16px] md:px-0">
              {history.map(item => (
                <ContinueCard key={item.series.id} item={item} />
              ))}
            </div>
          </section>
        )}

        {trending.length > 0 && (
          <section className="flex flex-col gap-[12px] md:gap-[18px]">
            <SectionHead title="Trending now" action={<span className="hidden md:inline">Top 5 this week</span>} />
            <div className="scrollbar-hide flex gap-[12px] overflow-x-auto px-[16px] md:grid md:grid-cols-5 md:gap-[16px] md:px-0">
              {trending.map(item => (
                <TrendingCard key={item.id} series={item} />
              ))}
            </div>
          </section>
        )}

        <section className="flex flex-col gap-[16px] md:gap-[18px]">
          {tabs.length > 0 && (
            <div className="scrollbar-hide flex items-end gap-[20px] overflow-x-auto px-[16px] md:gap-[24px] md:px-0">
              <UnderlineTab
                id="cinema-tab-for-you"
                label="For you"
                active={genre === null}
                onClick={() => setGenre(null)}
              />
              {tabs.map(category => (
                <UnderlineTab
                  key={category.id}
                  id={`cinema-tab-${category.id}`}
                  label={category.name}
                  active={genre === category.name}
                  onClick={() => setGenre(category.name)}
                />
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-[14px] gap-y-[20px] px-[16px] md:grid-cols-4 md:gap-x-[16px] md:gap-y-[28px] md:px-0 lg:grid-cols-6">
            {isLoading
              ? Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => <DramaCardDarkSkeleton key={i} />)
              : shown.map(item => <DramaCardDark key={item.id} series={item} genre={primaryGenre(item, categories)} />)}
          </div>
          {!isLoading && (
            <p className="px-[16px] pt-[8px] pb-[4px] text-center text-[13px] leading-[16px] font-medium text-neutral-400 md:pt-[18px] md:pb-[8px] md:text-[14px] md:leading-[17px]">
              More dramas are coming
            </p>
          )}
        </section>
      </div>
      {/* The footer's own background is an image; this is what shows until it loads, and on a
          black page a missing image would leave its dark text unreadable. */}
      <div className="bg-background-base">
        <Footer />
      </div>
    </PageContainer>
  );
}

function SectionHead({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between px-[16px] md:px-0">
      <h2 className="text-[18px] leading-[22px] font-bold text-neutral-50 md:text-[22px] md:leading-[26px]">{title}</h2>
      {action && (
        <p className="text-[13px] leading-[16px] font-medium text-neutral-400 md:text-[14px] md:leading-[17px]">
          {action}
        </p>
      )}
    </div>
  );
}
